/**
 * Le médiateur de la consommation, et l'obligation que presque personne ne connaît.
 *
 * Depuis le 1er janvier 2016, tout professionnel qui vend à des consommateurs
 * doit leur garantir l'accès gratuit à un médiateur de la consommation
 * (article L. 612-1 du code de la consommation). L'obligation ne regarde ni la
 * taille de l'entreprise ni son secteur : un micro-entrepreneur seul y est tenu
 * exactement comme une chaîne de magasins.
 *
 * DEUX OBLIGATIONS DISTINCTES, et c'est là que les dossiers pèchent :
 *   - DÉSIGNER un médiateur référencé par la commission d'évaluation et de
 *     contrôle de la médiation de la consommation, ce qui suppose un contrat et
 *     un coût, à la charge du professionnel ;
 *   - INFORMER le consommateur de son nom et de ses coordonnées, de manière
 *     visible et lisible, sur le site internet, dans les conditions générales
 *     et sur les bons de commande (articles L. 616-1 et R. 616-1).
 * Avoir un médiateur sans l'écrire nulle part ne met pas en règle, et écrire le
 * nom d'un médiateur qu'on n'a pas engagé encore moins.
 *
 * CE MODULE NE PRODUIT AUCUN DOCUMENT : il diagnostique. Le médiateur se
 * choisit sur la liste officielle, et aucune lettre ne remplace ce contrat.
 */

import { nettoyer } from "./documentText.js";

/** Depuis quand l'obligation s'applique. */
export const DEPUIS = "1er janvier 2016";

/** L'amende administrative encourue, selon la forme de l'entreprise. */
export const AMENDE = { personnePhysique: 3000, societe: 15000 };

/**
 * Les secteurs que la médiation de la consommation ne couvre pas.
 *
 * Ils ont leurs propres voies de recours, et proposer un médiateur de la
 * consommation ne les remplacerait pas.
 */
export const SECTEURS_EXCLUS = ["sante", "enseignementSuperieur", "serviceInteretGeneral"];

/** Les deux supports d'information que le règlement nomme. */
export const SUPPORTS = ["site", "conditions"];

/**
 * Qui est tenu, et qui ne l'est pas.
 *
 * Le seul critère qui compte est la qualité du client : la médiation de la
 * consommation règle les litiges entre un consommateur et un professionnel. Qui
 * ne facture que des entreprises n'a rien à désigner.
 */
export function concerne(reponses) {
    const r = reponses || {};
    if (!r.clients) return { concerne: null, motif: null };
    if (r.clients === "professionnels") return { concerne: false, motif: "entreProfessionnels" };
    if (!r.secteur) return { concerne: null, motif: null };
    if (SECTEURS_EXCLUS.includes(r.secteur)) return { concerne: false, motif: "secteurExclu" };
    return { concerne: true, motif: null };
}

/**
 * Ce qui manque pour être en règle, dans l'ordre où il faut s'en occuper.
 *
 * L'ordre n'est pas décoratif : désigner d'abord, écrire ensuite. Annoncer un
 * médiateur qu'on n'a pas engagé expose davantage que de n'en annoncer aucun,
 * puisque le consommateur qui le saisira sera éconduit.
 */
export function manquements(reponses) {
    const r = reponses || {};
    if (!concerne(r).concerne) return [];

    /* Sans médiateur désigné, il n'y a rien à constater du côté des mentions :
       on ne peut pas avoir écrit le nom de quelqu'un qu'on n'a pas engagé, et
       demander « où l'avez-vous écrit ? » à qui vient de répondre « nulle part,
       je n'en ai pas » n'a aucun sens. Les deux étapes sont annoncées dans
       l'ordre où elles se font. */
    if (r.mediateur !== "oui") return ["designer", "ecrireEnsuite"];

    const liste = [];
    // Le site n'est obligatoire que s'il existe : personne n'est tenu d'en avoir un.
    if (r.site === "oui" && r.mentionSite !== "oui") liste.push("mentionSite");
    if (r.mentionConditions !== "oui") liste.push("mentionConditions");
    return liste;
}

/** L'amende encourue, selon la forme déclarée. */
export function amendeEncourue(reponses) {
    return (reponses || {}).forme === "societe" ? AMENDE.societe : AMENDE.personnePhysique;
}

/**
 * Le diagnostic complet, tel que le parcours l'affiche.
 *
 * `enRegle` n'est vrai que si RIEN ne manque : une conformité partielle n'existe
 * pas, l'amende sanctionne le manquement, pas son ampleur.
 */
export function diagnostic(reponses, aujourdhui) {
    const r = reponses || {};
    const tri = concerne(r);
    if (tri.concerne !== true) return { concerne: false, motif: tri.motif, manques: [], enRegle: false };

    const manques = manquements(r);
    return {
        concerne: true,
        motif: null,
        manques,
        enRegle: manques.length === 0,
        amende: amendeEncourue(r),
        // La date sert au libellé « depuis X années », pas au calcul du droit.
        annees: Number(String(aujourdhui).slice(0, 4)) - 2016,
    };
}

/** Ce que le parcours récapitule avant les actions. */
export function recapitulatif(reponses, aujourdhui) {
    const etat = diagnostic(reponses, aujourdhui);
    if (!etat.concerne) return null;

    const oui = (valeur) => (valeur === "oui" ? "Oui" : "Non");
    const lignes = [
        ["Vos clients", reponses.clients === "particuliers" ? "Des particuliers" : "Des particuliers et des entreprises"],
        ["Médiateur désigné", oui(reponses.mediateur)],
    ];
    /* Les lignes de mentions ne s'affichent que si elles ont été demandées :
       sans médiateur, le parcours ne pose pas la question. */
    if (reponses.mediateur === "oui") {
        if (reponses.site === "oui") lignes.push(["Mention sur votre site", oui(reponses.mentionSite)]);
        lignes.push(["Mention dans vos conditions générales", oui(reponses.mentionConditions)]);
    }
    lignes.push(["Amende encourue, au plus", `${etat.amende.toLocaleString("fr-FR")} €`]);
    return lignes;
}

/** Le texte à recopier dans ses conditions générales, une fois le médiateur choisi. */
export function mentionType(reponses) {
    const r = reponses || {};
    const nom = nettoyer(r.nomMediateur);
    const site = nettoyer(r.siteMediateur);
    if (!nom) return "";

    return (
        "Conformément à l'article L. 612-1 du code de la consommation, vous pouvez " +
        "recourir gratuitement au service de médiation suivant en cas de litige : " +
        `${nom}${site ? `, ${site}` : ""}. ` +
        "La saisine du médiateur suppose d'avoir tenté au préalable de résoudre le " +
        "litige directement auprès de nous par une réclamation écrite."
    );
}
